

import ThreeD from "./ThreeD";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaLinkedin, FaWhatsapp } from "react-icons/fa";

import image2 from "./assets/image2.jpg";
import bslogo from "./assets/companylogo.png";
import image6 from "./assets/image6.jpg";

// ── Animated number counter ──────────────────────────────────────────────
function CountUp({ end, suffix = "", duration = 1600 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = null;

          const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / duration, 1);
            setVal(Math.floor(p * end));
            if (p < 1) requestAnimationFrame(step);
          };

          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

const Careers = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const heroRef = useRef(null);

  useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        size: `${6 + (i % 7) * 5}px`,
        left: `${(i * 3.4) % 100}%`,
        delay: `${(i % 8) * 0.85}s`,
        duration: `${10 + (i % 6) * 2.2}s`,
        opacity: 0.08 + (i % 5) * 0.04,
      })),
    []
  );

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        }),
      { threshold: 0.12 }
    );

    document
      .querySelectorAll(".reveal-up, .reveal-left, .reveal-right, .title-glow")
      .forEach((el) => obs.observe(el));

    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const fn = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });

    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const fn = () => {
      hero.style.transform = `translateY(${window.scrollY * 0.2}px)`;
    };

    window.addEventListener("scroll", fn, { passive: true });

    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const btns = document.querySelectorAll(".hero-btn, .apply-btn");

    const onMove = (e) => {
      const r = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width / 2) * 0.3;
      const y = (e.clientY - r.top - r.height / 2) * 0.3;
      e.currentTarget.style.transform = `translate(${x}px,${y}px) scale(1.04)`;
    };

    const onLeave = (e) => {
      e.currentTarget.style.transform = "";
    };

    btns.forEach((b) => {
      b.addEventListener("mousemove", onMove);
      b.addEventListener("mouseleave", onLeave);
    });

    return () =>
      btns.forEach((b) => {
        b.removeEventListener("mousemove", onMove);
        b.removeEventListener("mouseleave", onLeave);
      });
  }, []);

  const handleApplyNow = () => {
    alert("Thank you for your interest. We will receive responses soon.");
  };

  const jobs = [
    {
      title: "IT & Marketing Executive",
      desc: "Drive digital growth through targeted marketing campaigns and IT coordination. Manage online presence, client outreach, and tech-enabled marketing strategies to expand our brand reach.",
      icon: "📣",
      level: "Junior-Mid",
      type: "Full-time",
      perks: [
        { icon: "🍽️", label: "Food & Accommodation Provided" },
        { icon: "🛂", label: "Candidates with a valid passport will be preferred" },
      ],
    },
  ];

  const stats = [
    { label: "Team Members", end: 20, suffix: "+" },
    { label: "Projects Delivered", end: 10, suffix: "+" },
    { label: "Clients Served", end: 10, suffix: "+" },
    { label: "Years Active", end: 1, suffix: "+" },
  ];

  return (
    <div className="careers-page-root">
      <style>{`
        html,
        body {
          cursor: auto !important;
        }

        button,
        a,
        select,
        .apply-btn,
        .hero-btn {
          cursor: pointer !important;
        }

        canvas {
          pointer-events: none !important;
        }

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        :root {
          --text-main: #eef2f7;
          --text-soft: #94a3b8;
          --accent: #a3e635;
          --accent-2: #bef264;
          --accent-deep: #84cc16;
          --shadow-soft: 0 18px 45px rgba(15, 23, 42, 0.08);
          --shadow-card: 0 14px 35px rgba(15, 23, 42, 0.09);
          --heading-font: "Cinzel", serif;
          --body-font: "Manrope", sans-serif;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          font-family: var(--body-font);
          color: var(--text-main);
          background: #0a0e1a;
          overflow-x: hidden;
        }

        a {
          text-decoration: none;
          color: inherit;
        }

        .careers-page-root {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          background: #0a0e1a;
        }

        .careers-navbar,
        .careers-hero-section,
        .why-work-section,
        .jobs-section,
        .stats-section,
        .careers-footer {
          position: relative;
          z-index: 1;
        }

        @keyframes glowPulse {
          0%, 100% {
            text-shadow: 0 0 12px rgba(163, 230, 53, 0.1);
          }
          50% {
            text-shadow: 0 0 28px rgba(163, 230, 53, 0.32);
          }
        }

        @keyframes shineSweep {
          0% {
            transform: translateX(-120%) skewX(-15deg);
          }
          100% {
            transform: translateX(140%) skewX(-15deg);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.85) translateY(16px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes heroRingFloat {
          0% {
            transform: scale(1) rotate(0deg);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.14) rotate(18deg);
            opacity: 0.9;
          }
        }

        @keyframes ringPulse {
          0% {
            transform: scale(1);
            opacity: 0.6;
          }
          100% {
            transform: scale(1.7);
            opacity: 0;
          }
        }

        @keyframes statCountIn {
          from {
            opacity: 0;
            transform: perspective(600px) rotateY(-20deg) translateX(-20px);
          }
          to {
            opacity: 1;
            transform: perspective(600px) rotateY(0) translateX(0);
          }
        }

        @keyframes borderGlow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(163, 230, 53, 0);
          }
          50% {
            box-shadow: 0 0 0 3px rgba(163, 230, 53, 0.18);
          }
        }

        @keyframes perkPop {
          from {
            opacity: 0;
            transform: scale(0.8) translateY(6px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .careers-navbar {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: rgba(19, 26, 44, 0.88);
          backdrop-filter: blur(16px);
          border-bottom: 1px solid rgba(163, 230, 53, 0.12);
          box-shadow: 0 8px 28px rgba(15, 23, 42, 0.05);
          transition: padding 0.4s ease, box-shadow 0.4s ease, background 0.4s ease;
        }

        .careers-navbar.scrolled {
          background: rgba(19, 26, 44, 0.98);
          box-shadow: 0 12px 40px rgba(15, 23, 42, 0.14),
            0 1px 0 rgba(163, 230, 53, 0.2);
        }

        .careers-nav-container {
          width: min(1220px, 92%);
          margin: 0 auto;
          padding: 16px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: padding 0.4s ease;
        }

        .scrolled .careers-nav-container {
          padding: 10px 0;
        }

        .careers-nav-logo {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .careers-nav-logo img {
          width: 54px;
          height: 54px;
          object-fit: contain;
          border-radius: 14px;
          filter: drop-shadow(0 8px 18px rgba(163, 230, 53, 0.18));
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
            filter 0.3s ease;
        }

        .careers-nav-logo:hover img {
          transform: perspective(300px) rotateY(16deg) rotateX(-6deg) scale(1.1);
          filter: drop-shadow(0 14px 26px rgba(163, 230, 53, 0.38));
        }

        .scrolled .careers-nav-logo img {
          width: 44px;
          height: 44px;
        }

        .site-title {
          font-family: var(--heading-font);
          font-size: 1.45rem;
          font-weight: 800;
          letter-spacing: 1px;
          color: #eef2f7;
        }

        .back-button {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: none;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          color: #fff;
          font-size: 18px;
          cursor: pointer;
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
            box-shadow 0.3s ease;
          box-shadow: 0 10px 24px rgba(163, 230, 53, 0.22);
        }

        .back-button:hover {
          transform: perspective(300px) rotateY(-18deg) translateZ(8px) scale(1.12);
          box-shadow: 0 18px 38px rgba(163, 230, 53, 0.35);
        }

        .careers-nav-links {
          display: flex;
          align-items: center;
          gap: 22px;
        }

        .careers-nav-links a {
          color: var(--text-soft);
          font-size: 0.96rem;
          font-weight: 700;
          position: relative;
          transition: color 0.28s ease;
        }

        .careers-nav-links a:hover,
        .careers-nav-links a.active {
          color: var(--accent-deep);
        }

        .careers-nav-links a::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: -7px;
          width: 0%;
          height: 2px;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--accent), var(--accent-2));
          transition: width 0.3s ease;
        }

        .careers-nav-links a:hover::after,
        .careers-nav-links a.active::after {
          width: 100%;
        }

        .nav-toggle {
          display: none;
          font-size: 1.8rem;
          color: #eef2f7;
          cursor: pointer;
        }

        .careers-hero-section {
          width: min(1220px, 92%);
          margin: 32px auto 0;
        }

        .careers-hero {
          position: relative;
          min-height: 540px;
          border-radius: 30px;
          overflow: hidden;
          background: linear-gradient(
              135deg,
              rgba(0, 0, 0, 0.56),
              rgba(14, 14, 14, 0.35)
            ),
            url(${image6}) center/cover no-repeat;
          box-shadow: var(--shadow-soft);
          border: 1px solid rgba(163, 230, 53, 0.12);
          transform-style: preserve-3d;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(
              circle at 20% 30%,
              rgba(163, 230, 53, 0.18),
              transparent 28%
            ),
            linear-gradient(
              135deg,
              rgba(0, 0, 0, 0.55),
              rgba(0, 0, 0, 0.2)
            );
        }

        .hero-rings {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .hero-ring {
          position: absolute;
          border-radius: 50%;
          border: 1.5px solid rgba(163, 230, 53, 0.22);
          animation: heroRingFloat 10s ease-in-out infinite alternate;
        }

        .hero-ring:nth-child(1) {
          width: 320px;
          height: 320px;
          right: -50px;
          top: -50px;
        }

        .hero-ring:nth-child(2) {
          width: 200px;
          height: 200px;
          right: 40px;
          top: 40px;
          border-color: rgba(163, 230, 53, 0.15);
          animation-duration: 13s;
          animation-delay: 1.5s;
        }

        .hero-live-dot {
          position: absolute;
          bottom: 28px;
          right: 28px;
          z-index: 3;
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(163, 230, 53, 0.3);
          border-radius: 999px;
          padding: 8px 16px;
          color: #fff;
          font-size: 0.8rem;
          font-weight: 700;
        }

        .live-pulse {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--accent-2);
          position: relative;
        }

        .live-pulse::after {
          content: "";
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background: var(--accent-2);
          opacity: 0.5;
          animation: ringPulse 1.5s ease-out infinite;
        }

        .careers-hero-content {
          position: relative;
          z-index: 2;
          max-width: 700px;
          padding: 90px 60px;
          transform-style: preserve-3d;
        }

        .hero-heading,
        .section-heading {
          font-family: var(--heading-font);
          line-height: 1.1;
          letter-spacing: 0.8px;
        }

        .hero-heading {
          color: #fff;
          font-size: clamp(2.8rem, 5vw, 4.6rem);
          margin-bottom: 22px;
          display: block;
          transform: translateZ(55px);
        }

        .section-heading {
          color: #eef2f7;
          font-size: clamp(2rem, 3vw, 3rem);
          margin-bottom: 18px;
        }

        .section-heading::after {
          content: "";
          display: block;
          width: 64px;
          height: 4px;
          background: linear-gradient(90deg, var(--accent), var(--accent-2));
          border-radius: 3px;
          margin-top: 16px;
        }

        .hero-subtext {
          color: #f8fafc;
          line-height: 1.9;
          font-size: 1rem;
          margin-bottom: 24px;
        }

        .section-mini {
          display: inline-block;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 3px;
          color: var(--accent-deep);
          margin-bottom: 12px;
        }

        .hero-mini {
          color: var(--accent-2);
        }

        .title-glow {
          animation: glowPulse 4s ease-in-out infinite;
        }

        .hero-btn,
        .apply-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 180px;
          padding: 14px 24px;
          border-radius: 999px;
          color: white;
          font-weight: 800;
          letter-spacing: 0.4px;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          box-shadow: 0 12px 30px rgba(163, 230, 53, 0.28);
          position: relative;
          overflow: hidden;
          transition: box-shadow 0.28s ease;
          border: none;
          cursor: pointer;
        }

        .hero-btn {
          opacity: 0;
          animation: scaleIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) 1.2s forwards;
        }

        .hero-btn::before,
        .apply-btn::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 45%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(163, 230, 53, 0.28),
            transparent
          );
          transform: translateX(-120%) skewX(-15deg);
          animation: shineSweep 3.5s infinite;
        }

        .hero-btn:hover,
        .apply-btn:hover {
          box-shadow: 0 18px 38px rgba(163, 230, 53, 0.4);
        }

        .stats-section {
          width: min(1220px, 92%);
          margin: 42px auto 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          perspective: 1200px;
        }

        .stat-card {
          padding: 28px 20px;
          border-radius: 22px;
          text-align: center;
          background: linear-gradient(
            180deg,
            rgba(163, 230, 53, 0.18),
            rgba(19, 26, 44, 0.92)
          );
          border: 1px solid rgba(163, 230, 53, 0.3);
          box-shadow: var(--shadow-card);
          opacity: 0;
          transform: perspective(600px) rotateY(-20deg) translateX(-20px);
          transition: transform 0.12s ease, box-shadow 0.3s ease;
          animation: borderGlow 3s ease-in-out infinite;
        }

        .stat-card.visible {
          animation: statCountIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards,
            borderGlow 3s ease-in-out infinite 0.8s;
        }

        .stat-card:hover {
          transform: perspective(600px) translateY(-8px) rotateX(4deg) !important;
          box-shadow: 0 28px 48px rgba(163, 230, 53, 0.14),
            var(--shadow-card);
        }

        .stat-number {
          font-family: var(--heading-font);
          font-size: 2.4rem;
          font-weight: 800;
          color: var(--accent-deep);
          margin-bottom: 6px;
        }

        .stat-label {
          font-size: 0.88rem;
          font-weight: 600;
          color: var(--text-soft);
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .why-work-section,
        .jobs-section,
        .careers-footer {
          width: min(1220px, 92%);
          margin: 42px auto 0;
        }

        .why-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 42px;
          align-items: center;
        }

        .why-shell {
          padding: 36px 34px;
          border-radius: 28px;
          background: linear-gradient(
            180deg,
            rgba(19, 26, 44, 0.96),
            rgba(19, 26, 44, 0.88)
          );
          border: 1px solid rgba(163, 230, 53, 0.14);
          box-shadow: var(--shadow-card);
        }

        .why-text p {
          color: var(--text-soft);
          line-height: 1.9;
          font-size: 1rem;
          margin-top: 14px;
        }

        .image-frame {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: var(--shadow-card);
          min-height: 320px;
          background: #0a0e1a;
          transform-style: preserve-3d;
          transition: transform 0.4s ease, box-shadow 0.4s ease;
        }

        .image-frame img {
          width: 100%;
          height: 100%;
          min-height: 320px;
          object-fit: cover;
          display: block;
          transition: transform 0.55s ease, filter 0.45s ease;
        }

        .image-frame:hover {
          transform: perspective(900px) rotateY(5deg) rotateX(-2deg)
            translateZ(12px);
          box-shadow: -14px 22px 48px rgba(163, 230, 53, 0.16),
            0 32px 64px rgba(15, 23, 42, 0.14);
        }

        .image-frame:hover img {
          transform: scale(1.06);
          filter: brightness(1.04);
        }

        .open-positions {
          margin: 24px auto 0;
          display: grid;
          grid-template-columns: minmax(280px, 470px);
          justify-content: center;
          gap: 22px;
          perspective: 1400px;
        }

        .job-card {
          background: linear-gradient(
            180deg,
            rgba(19, 26, 44, 0.96),
            rgba(19, 26, 44, 0.88)
          );
          border: 1px solid rgba(163, 230, 53, 0.14);
          border-radius: 26px;
          padding: 28px 24px;
          position: relative;
          overflow: hidden;
          box-shadow: var(--shadow-card);
          transform-style: preserve-3d;
          cursor: default;
          transition: transform 0.18s ease, box-shadow 0.3s ease;
        }

        .job-card::after {
          content: "";
          position: absolute;
          inset: auto -30% -60% auto;
          width: 160px;
          height: 160px;
          background: radial-gradient(
            circle,
            rgba(163, 230, 53, 0.14),
            transparent 70%
          );
          pointer-events: none;
        }

        .job-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: -75%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(163, 230, 53, 0.3),
            transparent
          );
          transform: skewX(-15deg);
          pointer-events: none;
          z-index: 2;
        }

        .job-card:hover::before {
          animation: shineSweep 0.7s ease forwards;
        }

        .job-card:hover {
          transform: perspective(900px) translateY(-10px) rotateX(3deg)
            scale(1.01) !important;
          box-shadow: 0 28px 50px rgba(163, 230, 53, 0.16),
            var(--shadow-card) !important;
        }

        .job-card.featured {
          border: 1.5px solid rgba(163, 230, 53, 0.3);
        }

        .featured-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          color: #fff;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 1.5px;
          padding: 4px 12px;
          border-radius: 999px;
          margin-bottom: 14px;
          box-shadow: 0 6px 16px rgba(163, 230, 53, 0.22);
        }

        .featured-badge::before {
          content: "★";
          font-size: 0.7rem;
        }

        .perks-strip {
          display: flex;
          flex-direction: column;
          gap: 7px;
          margin: 14px 0 18px;
          padding: 14px 16px;
          background: rgba(163, 230, 53, 0.05);
          border: 1px solid rgba(163, 230, 53, 0.12);
          border-radius: 14px;
        }

        .perk-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.82rem;
          font-weight: 700;
          color: var(--accent-deep);
          opacity: 0;
          animation: perkPop 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .job-card.visible .perk-item:nth-child(1) {
          animation-delay: 0.1s;
        }

        .job-card.visible .perk-item:nth-child(2) {
          animation-delay: 0.2s;
        }

        .perk-icon {
          font-size: 1rem;
          flex-shrink: 0;
        }

        .job-icon {
          font-size: 1.8rem;
          margin-bottom: 12px;
          display: inline-block;
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .job-card:hover .job-icon {
          transform: rotate(12deg) scale(1.2) translateZ(20px);
        }

        .job-meta {
          display: flex;
          gap: 8px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        .job-tag {
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 1.5px;
          padding: 4px 10px;
          border-radius: 999px;
        }

        .job-tag.level {
          background: rgba(163, 230, 53, 0.1);
          color: var(--accent-deep);
          border: 1px solid rgba(163, 230, 53, 0.18);
        }

        .job-tag.type {
          background: rgba(15, 23, 42, 0.06);
          color: var(--text-soft);
          border: 1px solid rgba(15, 23, 42, 0.1);
        }

        .job-card h3 {
          font-family: var(--heading-font);
          color: #eef2f7;
          font-size: 1.22rem;
          margin-bottom: 10px;
        }

        .job-card p {
          color: var(--text-soft);
          line-height: 1.9;
          font-size: 1rem;
          margin-bottom: 18px;
        }

        .reveal-up {
          opacity: 0;
          transform: perspective(800px) rotateX(16deg) translateY(38px);
          transition: opacity 0.85s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .reveal-up.visible {
          opacity: 1;
          transform: perspective(800px) rotateX(0deg) translateY(0);
          animation: none;
        }

        .reveal-left {
          opacity: 0;
          transform: perspective(800px) rotateY(-22deg) translateX(-55px);
          transition: opacity 0.85s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .reveal-left.visible {
          opacity: 1;
          transform: perspective(800px) rotateY(0deg) translateX(0);
          animation: none;
        }

        .reveal-right {
          opacity: 0;
          transform: perspective(800px) rotateY(22deg) translateX(55px);
          transition: opacity 0.85s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .reveal-right.visible {
          opacity: 1;
          transform: perspective(800px) rotateY(0deg) translateX(0);
          animation: none;
        }

        .careers-footer {
          margin-bottom: 34px;
        }

        .footer-container {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 60px;
          align-items: start;
          background: linear-gradient(
            180deg,
            rgba(19, 26, 44, 0.96),
            rgba(19, 26, 44, 0.88)
          );
          border: 1px solid rgba(163, 230, 53, 0.14);
          border-radius: 28px;
          padding: 34px;
          box-shadow: var(--shadow-card);
        }

        .footer-col {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          transition: transform 0.35s ease;
        }

        .footer-col:hover {
          transform: perspective(700px) translateZ(12px);
        }

        .footer-col h3 {
          font-family: var(--heading-font);
          color: #eef2f7;
          font-size: 1.1rem;
          margin-bottom: 12px;
        }

        .footer-col ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
        }

        .footer-col li a,
        .footer-col p {
          color: var(--text-soft);
          line-height: 1.9;
          font-size: 1rem;
          display: inline-block;
          transition: color 0.25s ease, transform 0.25s ease;
        }

        .footer-col li a:hover {
          color: var(--accent-deep);
          transform: translateX(6px);
        }

        .social-links {
          display: flex;
          gap: 14px;
          margin-top: 16px;
          flex-wrap: wrap;
        }

        .social-links a {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          color: var(--accent-deep);
          background: rgba(163, 230, 53, 0.08);
          border: 1px solid rgba(163, 230, 53, 0.14);
          transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
            background 0.28s ease, box-shadow 0.28s ease, color 0.28s ease;
        }

        .social-links a:hover {
          transform: perspective(300px) rotateX(-14deg) translateY(-8px)
            translateZ(14px);
          color: #fff;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          box-shadow: 0 16px 30px rgba(163, 230, 53, 0.28);
        }

        .center-text {
          text-align: center;
        }

        @media (max-width: 1100px) {
          .why-container,
          .footer-container {
            grid-template-columns: 1fr;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .nav-toggle {
            display: block;
          }

          .careers-nav-links {
            position: absolute;
            top: 82px;
            right: 4%;
            width: 240px;
            display: none;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            padding: 20px;
            background: rgba(19, 26, 44, 0.98);
            border-radius: 18px;
            border: 1px solid rgba(163, 230, 53, 0.14);
            box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
          }

          .careers-nav-links.active {
            display: flex;
          }

          .careers-hero {
            min-height: 450px;
          }

          .careers-hero-content {
            padding: 70px 24px;
          }

          .hero-heading {
            font-size: 2.2rem;
          }

          .section-heading {
            font-size: 1.8rem;
          }

          .open-positions,
          .stats-grid,
          .footer-container {
            grid-template-columns: 1fr;
          }

          .why-shell {
            padding: 24px 22px;
          }

          .footer-container {
            padding: 26px;
            gap: 34px;
          }
        }

        @media (max-width: 480px) {
          .site-title {
            font-size: 1.15rem;
          }

          .careers-nav-logo img {
            width: 46px;
            height: 46px;
          }

          .hero-btn,
          .apply-btn {
            width: 100%;
            min-width: auto;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .reveal-up,
          .reveal-left,
          .reveal-right {
            transform: none !important;
            transition: opacity 0.4s ease !important;
          }
        }

        /* ── UNIFIED SITE FOOTER (matches Tunga Drones page) ── */
        .tunga-footer {
          background: linear-gradient(180deg, rgba(163, 230, 53, 0.08), #070a12 18%);
          color: white;
          padding: 60px 5%;
          margin-top: 80px;
          border-top: 3px solid var(--accent);
        }

        .tunga-footer-content {
          width: min(1220px, 92%);
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 50px;
          margin-bottom: 40px;
        }

        .tunga-footer-col h3 {
          font-family: var(--heading-font);
          font-size: 1.3rem;
          margin-bottom: 20px;
          color: var(--accent-2);
        }

        .tunga-footer-col p, .tunga-footer-col a {
          font-size: 0.95rem;
          line-height: 1.8;
          color: rgba(255,255,255,0.8);
          text-decoration: none;
        }

        .tunga-footer-col a:hover { color: var(--accent-2); }

        .tunga-social-links {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .tunga-social-links a {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(163, 230, 53,0.2);
          display: grid;
          place-items: center;
          transition: all 0.3s ease;
          color: var(--accent-2);
        }

        .tunga-social-links a:hover {
          background: var(--accent);
          color: white;
          transform: translateY(-4px);
        }
      `}</style>

      <ThreeD />

      <header className={`careers-navbar${navScrolled ? " scrolled" : ""}`}>
        <div className="careers-nav-container">
          <div className="careers-nav-logo">
            <img src={bslogo} alt="Bliss Sierra Logo" />
            <h1 className="site-title">Bliss Sierra</h1>
          </div>

          <div className="nav-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            ☰
          </div>

          <nav className={`careers-nav-links${menuOpen ? " active" : ""}`}>
            <NavLink to="/" onClick={() => setMenuOpen(false)}>
              Home
            </NavLink>
            <NavLink to="/Tunga-Drones" onClick={() => setMenuOpen(false)}>
              Tunga Drones
            </NavLink>
            <NavLink to="/Our-Projects" onClick={() => setMenuOpen(false)}>
              Our Projects
            </NavLink>
            <NavLink to="/Services" onClick={() => setMenuOpen(false)}>
              Services
            </NavLink>
            <NavLink to="/Careers" onClick={() => setMenuOpen(false)}>
              Careers
            </NavLink>
            <NavLink to="/OurTeam" onClick={() => setMenuOpen(false)}>
              Our Core Team
            </NavLink>
          </nav>
        </div>
      </header>

      <section className="careers-hero-section">
        <div ref={heroRef}>
          <div className="careers-hero">
            <div className="hero-overlay" />
            <div className="hero-rings">
              <div className="hero-ring" />
              <div className="hero-ring" />
            </div>

            <div className="careers-hero-content">
              <p className="section-mini hero-mini">CAREERS</p>
              <h2 className="hero-heading title-glow visible">
                Build Your Future With A Team That Values Growth
              </h2>
              <p className="hero-subtext">
                Join Bliss Sierra and work on meaningful digital solutions in an
                environment focused on innovation, professionalism, and long-term
                career development.
              </p>

            </div>

            <div className="hero-live-dot">
              <span className="live-pulse" />
              Now Hiring
            </div>
          </div>
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-grid">
          {stats.map((s, i) => (
            <div className="stat-card reveal-up" key={i}>
              <div className="stat-number">
                <CountUp end={s.end} suffix={s.suffix} />
              </div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="why-work-section">
        <div className="why-container">
          <div className="why-shell reveal-up">
            <p className="section-mini">WHY WORK WITH US</p>
            <h2 className="section-heading title-glow">Grow With Bliss Sierra</h2>

            <div className="why-text">
              <p>
                At BlissSierra, we are committed to building a workplace that
                values professionalism, innovation, and continuous growth.
              </p>
              <p>
                Our teams collaborate on high-impact projects across cybersecurity,
                web development, and digital solutions, delivering reliable and
                scalable results for our clients.
              </p>
              <p>
                We promote a culture of learning, accountability, and excellence,
                where employees are empowered to develop their skills and advance
                their careers.
              </p>
              <p>
                At BlissSierra, you will have the opportunity to contribute to
                meaningful work while growing within a forward-thinking and
                supportive organization.
              </p>
            </div>
          </div>

          <div className="image-frame reveal-right">
            <img src={image2} alt="Work With Us" />
          </div>
        </div>
      </section>

      <section className="jobs-section">
        <p className="section-mini center-text">OPEN POSITIONS</p>
        <h2 className="section-heading center-text title-glow">
          Current Opportunities
        </h2>

        <div className="open-positions">
          {jobs.map((job, idx) => (
            <div key={idx} className="job-card reveal-up featured">
              <div className="featured-badge">NEW OPENING</div>

              <div className="job-icon">{job.icon}</div>

              <div className="job-meta">
                <span className="job-tag level">{job.level}</span>
                <span className="job-tag type">{job.type}</span>
              </div>

              <h3>{job.title}</h3>
              <p>{job.desc}</p>

              <div className="perks-strip">
                {job.perks.map((perk, pIdx) => (
                  <div className="perk-item" key={pIdx}>
                    <span className="perk-icon">{perk.icon}</span>
                    <span>{perk.label}</span>
                  </div>
                ))}
              </div>

              <button className="apply-btn" onClick={handleApplyNow}>
                Apply Now
              </button>
            </div>
          ))}
        </div>
      </section>

      <footer className="tunga-footer">
        <div className="tunga-footer-content">
          <div className="tunga-footer-col reveal-left">
            <h3>Bliss Sierra</h3>
            <p>We are committed to delivering the best services for your business.</p>
          </div>
          <div className="tunga-footer-col reveal-up">
            <h3>Quick Links</h3>
            <NavLink to="/">Home</NavLink><br/>
            <NavLink to="/Tunga-Drones">Tunga Drones</NavLink><br/>
            <NavLink to="/Our-Projects">Our Projects</NavLink><br/>
            <NavLink to="/Services">Services</NavLink><br/>
            <NavLink to="/Careers">Careers</NavLink><br/>
            <NavLink to="/OurTeam">Our Core Team</NavLink>
          </div>
          <div className="tunga-footer-col reveal-right">
            <h3>Contact</h3>
            <p>📍 Neelakanta Nilayam, Kakateeya Hiils, Road No 9, Madhapur, Hyderabad-500081</p>
            <p>📧 contact@blisssierra.com</p>
            <p>📞 +91-7702232247</p>
            <div className="tunga-social-links">
              <a href="https://www.facebook.com/profile.php?id=61584931498008" target="_blank" rel="noreferrer" aria-label="Facebook"><FaFacebookF /></a>
              <a href="https://www.instagram.com/blisssierra1/" target="_blank" rel="noreferrer" aria-label="Instagram"><FaInstagram /></a>
              <a href="https://www.linkedin.com/feed/" target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
              <a href="https://wa.me/917702232247" target="_blank" rel="noreferrer" aria-label="WhatsApp"><FaWhatsapp /></a>
            </div>
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "30px", textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
          <p>&copy; 2026 Bliss Sierra. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Careers;